/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import { timer } from "rxjs";
import { ReactQuillProps } from "@rsupport/react-quill";
import "quill/dist/quill.snow.css";
import "quill-mention";

import { _T } from "@rfice/language";
import { useEventHandler } from "@rfice/eventhandler";
import { useModal } from "@/frameworks/designStuff/components";
import { LocalData } from "@/frameworks/libraries/storage";
import { useMiniChatPopupManager } from "@/frameworks/core/hooks";
import { EventID, ModalID, ZIndex } from "@/frameworks/core/commons";
import { Properties } from "@/frameworks/libraries";
import { useChatDataStore } from "@/modules/chat/store";

import ChatEditor, { ChatEditorRefType } from "./ChatEditor";
import {
	UploadedFiles,
	ChatButtons,
	Emoticons,
	useChatFiles,
	useChatInputState,
	LinkPopup,
	ChatInputContext
} from "./private";
import * as S from "./private/emotion";
import ChatVote from "./ChatVote";
import { MENTION_ALL, ID_MENTION_ALL } from "./private";

interface ChatInputProps extends Omit<ReactQuillProps, "defaultValue"> {
	defaultValue?: ChatServices.Format2.Container.Richtext | null;
	placeholder?: string;
	className?: string;
	atMentions?: { id: string; value: string }[];
	/**
	 * 멘션 기능중 @all 멘션 활성화 여부
	 */
	disableAllTagMention?: boolean;
	hashMentions?: { id: string; value: string }[];
	disabled?: boolean;
	onSubmit?: (
		line: ChatServices.Format2.Message,
		files?: ChatFileFormat[],
		isEmergency?: boolean,
		plainText?: string
	) => void;
	onCancel?: () => void;
	onTyping?: () => void;
	onChange?: (text: string) => void;
	update?: boolean;
	variant?: ChatUI.Size;
	myRoomID?: string;
	spaceID?: string;
	threadID?: string;
	type: "Space" | "DM" | "CONFERENCE";
	channelID: string;
	isMyRoom: boolean;
	isHideSiren?: boolean;
	isHideVote?: boolean;
}

type ChatFileFormat = (
	| ChatServices.Format2.Elements.File
	| ChatServices.Format2.Elements.Image
	| ChatServices.Format2.Elements.Link
) & { fileBoxID: string };

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOGTAG = "#ChatInput#";

function ChatInputComponent({
	className,
	defaultValue,
	disableAllTagMention = false,
	atMentions = [],
	children,
	...rest
}: ChatInputProps): JSX.Element {
	const theme = useTheme();
	const modalActions = useModal();
	const [text, setText] = useState("");
	const [composition, setComposition] = useState(false);

	const editor = useRef<ChatEditorRefType>(null);
	const submitButton = useRef<HTMLButtonElement>(null);
	const divRef = useRef<HTMLDivElement>(null);

	const atMentionList =
		atMentions.length < 2
			? atMentions
			: disableAllTagMention
				? atMentions
				: [{ id: ID_MENTION_ALL, value: _T(MENTION_ALL) }, ...atMentions];

	const { fileMap, localFileMap, emoticon, actions } = useChatFiles({
		...rest,
		roomID: rest.channelID
	});

	const {
		disabled,
		isEmergency,
		actions: { setEmergency }
	} = useChatInputState({ text, emoticon, fileMap, forceDisabled: rest.disabled, composition });

	const reset = () => {
		actions.clearFiles();
		setEmergency(false);
		editor.current?.clearContents();
		divRef.current?.scrollIntoView({ block: "center" });
	};

	const handleCacheCurrentEmot = (richText: ChatServices.Format2.Container.Richtext | null) => {
		const emot = LocalData.get("cel");
		const elmentEmotion = richText?.elements.filter((item) => item?.type === "emoticon");

		if (!elmentEmotion || (elmentEmotion && elmentEmotion[0]?.type !== "emoticon")) {
			return;
		}

		let img: string = "";
		if (elmentEmotion && elmentEmotion[0]?.type === "emoticon") {
			img = elmentEmotion[0]?.text;
		}

		const recentEmotion: string[] = emot ? emot : [];
		const index = recentEmotion?.indexOf(img);
		if (index > -1) {
			recentEmotion.splice(index, 1);
		}

		recentEmotion.unshift(img);
		LocalData.set("cel", recentEmotion);
	};

	const handleSubmit = () => {
		if (!editor.current || disabled) return;
		const plainText = editor?.current?.getContents(false).text;
		actions.getCurrentRichText(editor.current).then(({ richText, files }) => {
			if (richText) {
				handleCacheCurrentEmot(richText);
				rest.onSubmit?.(richText, files, isEmergency, plainText);
				reset();
			}
		});
	};

	const handleFileDropped = useCallback(
		async ({ channelID, fileList, variant }: { channelID: string; fileList: File[]; variant: string }) => {
			if (rest.variant !== variant) return;
			if (channelID !== rest.channelID) return;

			actions.uploadFileWithTus(fileList);
		},
		[rest.channelID, actions.uploadFileWithTus]
	);

	const handleVote = () => {
		modalActions.show({
			id: ModalID.CHAT_VOTE,
			content: <ChatVote roomID={rest.channelID ?? rest.spaceID} />,
			zIndex: ZIndex.SIDEBAR + 1
		});
	};

	const { miniChat: miniChatValue } = useMiniChatPopupManager();

	const roomUIState = useChatDataStore((state) => state.room_ui[rest.channelID]);
	const miniChat = miniChatValue(rest.channelID, rest.spaceID);

	useEffect(() => {
		reset();
	}, [rest.channelID]);

	useEffect(() => {
		if (disabled) return;
		if (miniChat?.focus) {
			editor.current?.focus();
		}
	}, [miniChat?.focus, disabled]);

	useEffect(() => {
		if (rest.disabled) return;
		if (roomUIState?.isReplying) {
			editor.current?.focus();
		}
	}, [roomUIState?.isReplying, rest.disabled]);

	useEffect(() => {
		if (!editor.current) return;
		if (defaultValue) {
			editor.current?.setText(defaultValue);
			const currentText = editor.current.getText();
			editor.current.getEditor()?.setSelection(currentText?.length ?? 0, 0);
		}
	}, [defaultValue]);

	useEventHandler(EventID.DROP_CHAT_FILES, handleFileDropped, [handleFileDropped], {
		disposing: false
	});

	useEventHandler(EventID.UPDATE_EMERGENCY, ({ isEmergency }: { isEmergency: boolean }) => {
		setEmergency(isEmergency);
	});

	const isHasFile = fileMap && Object.values(fileMap).length > 0;
	const hasOnlineFile = localFileMap && Object.values(localFileMap).length > 0;
	const isGuest = Properties.property.isGuest;

	return (
		<ChatInputContext.Provider value={{ editor: editor.current }}>
			<S.StyledChatInput
				variant={rest.variant}
				forceDisable={rest.disabled}
				update={rest.update}
				className={`w-100 h-full ${className}`}
				ref={divRef}
				onMouseUp={() => {
					editor.current?.focus();
				}}
			>
				{emoticon && <Emoticons emoticon={emoticon} onRemove={actions.removeEmoticon} />}
				{isHasFile && <UploadedFiles files={Object.values(fileMap)} onRemove={actions.removeFile} />}
				{hasOnlineFile && <UploadedFiles localFiles={localFileMap} />}
				<ChatEditor
					ref={editor}
					atMentions={atMentionList}
					onEntered={() => {
						timer(0).subscribe(() => {
							// console.debug(LOGTAG, "onEntered", submitButton.current, rest.disabled, disabled);
							submitButton.current?.click();
						});
					}}
					editor={{
						readOnly: rest.disabled || (isGuest && rest.variant !== "transparent")
					}}
					editorStyle={{
						mentionList: S.mentionListCSS,
						mentionItem: S.mentionItemCSS(theme),
						container: isEmergency ? S.sirenCSS(theme) : undefined,
						typewriter: isEmergency ? S.sirenEditorCSS(theme) : S.editorCSS(isHasFile ? 4 : 8),
						formats: S.formatsCSS(theme),
						editor: S.radiusEditor(theme, rest.variant ?? "fullSize", rest.update),
						toolbar: S.toolbarCSS(rest.variant, rest.update)
					}}
					onLinkRequest={(submit) => {
						modalActions.show({
							id: ModalID.CHAT_LINK_POPUP,
							content: <LinkPopup onConfirm={submit} modalID={ModalID.CHAT_LINK_POPUP} />
						});
					}}
					onChange={(text) => {
						rest.onChange?.(text);
						setText(text);
						rest.onTyping?.();
					}}
					onPasteFile={(ev) => {
						actions.uploadFileWithTus([...ev.clipboardData.files]);
					}}
					isEmergency={isEmergency}
					placeholder={_T("메시지 입력")}
					roomID={rest.threadID ? rest.threadID : rest.channelID ?? rest.spaceID}
					set_composition={(state) => setComposition(state)}
					variant={rest.variant}
				/>
				{children}
				<ChatButtons
					update={rest.update}
					variant={rest.variant}
					anchorElement={divRef.current}
					disabled={rest.disabled || disabled || hasOnlineFile}
					onCancel={rest.onCancel}
					onSubmit={handleSubmit}
					onClickVote={handleVote}
					onUploadFile={(ev) => {
						actions.uploadFileWithTus(ev.target.files);
						ev.target.value = "";
					}}
					isEmergency={isEmergency}
					onClickSiren={() => setEmergency(!isEmergency)}
					onSelectEmoticon={(emoji) => {
						if (!editor.current) return;
						actions.addEmojiOrEmoticon(emoji, editor.current);
					}}
					ref={submitButton}
					isHideSiren={rest?.isHideSiren}
					isHideVote={rest?.isHideVote}
				/>
			</S.StyledChatInput>
		</ChatInputContext.Provider>
	);
}

const ChatInput = React.memo(ChatInputComponent);
ChatInput.displayName = "ChatInput";
export default ChatInput;
